// Room design document (CIN-15118): how an auditorium looks, as opposed to where its seats are.
// Saved as JSON on the cinema room (`cinema_room.design`), edited in the Room Studio, rendered by
// the studio's 3D view and by the guest-facing 3D seat map. Pure data and pure functions only, no
// runtime imports, so node --test can load it.
//
// Compatibility rule: this document outlives every release that writes it. `normalizeRoomDesign`
// must accept anything an older (or newer) studio saved -- unknown keys are dropped, unknown ids
// fall back to the default, numbers are clamped -- and must never throw.

export type HexColor = `#${string}`;

/** Size (mm) of a room whose shape was never stored; the studio and the guest map must agree on it. */
export const DEFAULT_ROOM_SIZE_MM = { width: 15000, depth: 20000, height: 5000 } as const;

/**
 * `seatShell` is the hard part of a two-tone seat (a wooden back shell around a fabric cushion); it only shows
 * when `seats.twoTone` is on, and reads as a darker shade of `seats` in documents saved before it existed.
 */
export const SURFACE_IDS = ['walls', 'floor', 'ceiling', 'curtain', 'seats', 'stage', 'seatShell'] as const;
export type SurfaceId = (typeof SURFACE_IDS)[number];

/**
 * Procedural textures are painted on a canvas at runtime (room-3d/textures), so the catalog ships
 * without image files. `image` uses `SurfaceMaterial.imageUrl`, an operator upload.
 */
export const TEXTURE_IDS = [
    'plain',
    'velvet',
    'fabric-weave',
    'leather',
    'quilted',
    'carpet-dots',
    'carpet-stars',
    'carpet-waves',
    'wood-panels',
    'parquet',
    'concrete',
    'acoustic-panels',
    'wood-slats',
    'brick',
    'marble',
    'art-deco',
    'starfield',
    'image'
] as const;
export type TextureId = (typeof TEXTURE_IDS)[number];

export type SurfaceMaterial = {
    texture: TextureId;
    /** Base color; procedural textures are painted in this color. */
    color: HexColor;
    /** Secondary color for patterned textures (carpet motif, panel grooves, stars). */
    accent: HexColor;
    /** Texture repeat multiplier, 0.25..4 (1 = the texture's natural scale in meters). */
    scale: number;
    /** 0 = mirror, 1 = matte. */
    roughness: number;
    /** CDN URL of an uploaded image, used when `texture` is `image`. */
    imageUrl: string | null;
};

export const LIGHTING_PRESETS = ['house', 'show', 'intermission', 'cleaning'] as const;
export type LightingPreset = (typeof LIGHTING_PRESETS)[number];

/** `footstool` (CIN-15402): a bookable padded ottoman on a short post in front of lounge recliners, no back. */
export const SEAT_STYLES = ['classic', 'recliner', 'sofa', 'bench', 'footstool'] as const;
export type SeatStyle = (typeof SEAT_STYLES)[number];

/**
 * How soft the seat's edges are: `square` is the plain box silhouette, `soft` (the default) rounds every
 * cushion, back and arm, `round` makes them pillow-like. Applied to the seat geometry by `rounded-box.ts`.
 */
export const SEAT_ROUNDNESS = ['square', 'soft', 'round'] as const;
export type SeatRoundness = (typeof SEAT_ROUNDNESS)[number];
export const DEFAULT_SEAT_ROUNDNESS: SeatRoundness = 'soft';

/** How the seats are drawn beyond their style: recliners show reclined, two materials, number plates, side panels. */
export const SEAT_POSES = ['upright', 'reclined'] as const;
export type SeatPose = (typeof SEAT_POSES)[number];

export type SeatDetails = {
    /** Recliners are drawn with the back tilted and the footrest out; other styles ignore it. */
    pose: SeatPose;
    /** A shell (the `seatShell` surface) around the cushion instead of one material. */
    twoTone: boolean;
    /** The seat number on a plate on the headrest. */
    headrestNumbers: boolean;
    /** Side panels at the ends of rows and beside aisles. */
    endPanels: boolean;
};

/**
 * Extras of the recliner style (CIN-15126); other styles ignore them, so a room that never picked a recliner does
 * not change. `cupholders` (on by default) lights a ring in the top of an armrest in the accent color, one per
 * seat; `trayTables` (off) adds a swivel tray on a short arm on the other armrest.
 */
export type SeatExtras = {
    trayTables: boolean;
    cupholders: boolean;
};

/**
 * How one part (cushion or shell) of a seat looks when it differs from the room: any of color, texture and accent.
 * A missing field keeps the next level down (seat, then category, then the design's `surfaces.seats` / `seatShell`),
 * field by field. `image` is not offered per seat: an upload belongs to a room surface.
 */
export type SeatPartMaterial = { color?: HexColor; texture?: TextureId; accent?: HexColor };

/**
 * The form of one seat, field by field (CIN-15126): everything about how a seat is drawn besides its materials. A
 * missing field keeps the next level down (seat, then category, then the room default: the design's `seatStyle`,
 * `seatRoundness`, `seats` and `seatExtras`, which have no editor of their own). `pose`, `cupholders` and
 * `trayTables` only count for the recliner style.
 */
export type SeatFormSpec = {
    style?: SeatStyle;
    roundness?: SeatRoundness;
    pose?: SeatPose;
    twoTone?: boolean;
    headrestNumbers?: boolean;
    endPanels?: boolean;
    cupholders?: boolean;
    trayTables?: boolean;
};

/** The material and form of one seat; a missing part or field keeps the next level down. */
export type SeatMaterialSpec = SeatFormSpec & { cushion?: SeatPartMaterial; shell?: SeatPartMaterial };

/**
 * Seats in one room can differ in color, texture and form (CIN-15126). Keys are server ids as strings: a seat's own
 * entry beats its category's, which beats the design's `surfaces.seats` / `surfaces.seatShell` (materials) and its
 * room-level `seatStyle`, `seatRoundness`, `seats`, `seatExtras` (form). Ids of seats and categories that no longer
 * exist are harmless (ignored when drawn); the studio prunes them when it saves. Saved as `seatMaterials` (the name
 * predates the form fields, which are optional on the same entries, so older documents read unchanged); documents
 * from before textures saved `seatColors` with a hex per part, which still reads.
 */
export type SeatMaterials = {
    byCategory: Record<string, SeatMaterialSpec>;
    bySeat: Record<string, SeatMaterialSpec>;
};

/** The on/off fields of a seat form, in the order they are saved. */
export const SEAT_FORM_FLAGS = ['twoTone', 'headrestNumbers', 'endPanels', 'cupholders', 'trayTables'] as const;

/** More distinct seat forms than this in one room and the least used fall back to the room default (see seat-forms). */
export const MAX_SEAT_FORMS = 16;

// The server bounds the whole design at 64 KB, so the per-seat entries are bounded by size as well as by count: a
// fully specified entry is ~70 bytes, and the studio refuses a stroke that would go past the budget.
export const MAX_SEAT_MATERIAL_ENTRIES = 1000;
export const MAX_CATEGORY_MATERIAL_ENTRIES = 200;
export const MAX_SEAT_MATERIAL_BYTES = 36_000;
export const MAX_CATEGORY_MATERIAL_BYTES = 10_000;

export const SCREEN_MASKINGS = ['none', 'flat', 'scope'] as const;
export type ScreenMasking = (typeof SCREEN_MASKINGS)[number];

/** What crowns a guard wall (barrier) at a split level or a balcony front. */
export const BARRIER_CAPS = ['none', 'handrail', 'brass', 'glass'] as const;
export type BarrierCap = (typeof BARRIER_CAPS)[number];

/** How the stepped floor of a stadium room is finished (CIN-15126). */
export type TierFinish = {
    /** Shows the guard walls derived at steep risers (split levels) and the railings of cross aisles. */
    derivedBarriers: boolean;
    barrierCap: BarrierCap;
    /** A lit strip along the guard wall. */
    barrierLed: boolean;
    /** Solid side walls under the outer rail of staircases that run along a wall. */
    stairSideWalls: boolean;
    /** Derives the aisle stairs, their gaps and handrails from the rows; off = none (stairs objects stand in for them). */
    autoStairs: boolean;
    /** Raised platforms that do not run wall to wall or have their own depth; absent = every platform as derived. */
    platforms?: TierPlatformSpec[];
    /** The step lights under the riser edges (and the aisle lights): their own color and look; absent = accent strips. */
    led?: TierLed;
};

/** `strip` is a thin lit band under each riser edge, `underglow` a soft glow fading down the riser face. */
export const TIER_LED_LOOKS = ['strip', 'underglow'] as const;
export type TierLedLook = (typeof TIER_LED_LOOKS)[number];

export type TierLed = {
    /** Absent = `lighting.accentColor`. */
    color?: HexColor;
    look: TierLedLook;
};

/**
 * The extent of one raised platform (CIN-15200), mm. A platform is named by a row standing on it (the studio writes
 * its front row): row names survive a new rake, where the platform's height would not. A row that no longer exists
 * leaves a harmless entry. Absent fields keep what the seats derive: wall to wall, the walkway in front.
 */
export type TierPlatformSpec = {
    row: string;
    /** The balcony the row stands on; absent = the main floor. */
    level?: string;
    /** How far the platform stops short of the left wall; it never ends closer to its outermost seat than a margin. */
    left?: number;
    right?: number;
    /** Floor between the front of the row's seats and the riser, at most up to the row in front. */
    front?: number;
    /** The color of this platform's riser light instead of the room's. */
    led?: { color: HexColor };
};

export const MAX_TIER_PLATFORMS = 200;

/**
 * Light and wall features (CIN-15126): LED line art, lit bands, oval windows, wall lamps, ceiling frames and coves,
 * downlight grids, lit pilasters, soffits, wall panels and side curtains; LED lattices on walls and ceilings and
 * coffered ceilings (CIN-15402). Positions along a wall are millimeters from
 * the start of the wall run (`s`, the wall = the outline edge named by its start vertex id) and above the floor (`z`).
 * Colors default to the lighting's accent color. Walls named by a feature that the room does not have are ignored
 * when drawn.
 */
export const WALL_FEATURE_KINDS = [
    'led-line',
    'light-band',
    'oval-window',
    'wall-lights',
    'led-lattice',
    'light-frame',
    'downlights',
    'cove',
    'ceiling-lattice',
    'coffers',
    'soffit',
    'pilasters',
    'wall-panels',
    'curtain'
] as const;
export type WallFeatureKind = (typeof WALL_FEATURE_KINDS)[number];

/**
 * The cells of an LED lattice (CIN-15402): lit triangles, diamonds, hexagons or chevrons framed by dark beams, or
 * `wash`: no cells, the area lit evenly (a colored light wash on a plain wall).
 */
export const LATTICE_PATTERNS = ['triangle', 'diamond', 'hex', 'chevron', 'wash'] as const;
export type LatticePattern = (typeof LATTICE_PATTERNS)[number];
/**
 * How the palette is spread over the cells: `random` per cell, `along` in ragged blocks along the run, `diagonal` in
 * diagonal stripes, `gradient` blended from the first color to the last along the run, `facing` by the way a cell
 * points (the even colors on cells pointing up, or along the run when turned to `columns`, the odd ones on the
 * others; several colors of a side blend from the bottom to the top), `within` blended across every cell.
 */
export const LATTICE_SPREADS = ['random', 'along', 'diagonal', 'gradient', 'facing', 'within'] as const;
export type LatticeSpread = (typeof LATTICE_SPREADS)[number];
export const MAX_LATTICE_COLORS = 6;
/** The look shared by a wall and a ceiling lattice. Lengths in mm; `glow` 0..1 is how much brighter the rim of a cell is than its middle. */
export type LatticeLook = {
    pattern: LatticePattern;
    cell: number;
    beam: number;
    beamColor: HexColor;
    /** 1..`MAX_LATTICE_COLORS` colors. */
    palette: HexColor[];
    spread: LatticeSpread;
    glow: number;
    seed: number;
    /** The cells turned a quarter: continuous vertical beams, triangles pointing sideways (the IMAX light wall). */
    columns?: true;
    /** The color the middle of every cell fades to; absent = a darker shade leaning toward the next palette color. */
    inner?: HexColor;
    /** Small bolt plates where the beams meet. */
    nodes?: true;
};
/** What lights a coffer: nothing, a strip around its rim, or its whole panel. */
export const COFFER_LIGHTS = ['none', 'rim', 'panel'] as const;
export type CofferLight = (typeof COFFER_LIGHTS)[number];
/** Where a coffer ceiling's beam LEDs run: along every main beam, or only over the third nearest the screen. */
export const BEAM_LED_ZONES = ['all', 'front'] as const;
export type BeamLedZone = (typeof BEAM_LED_ZONES)[number];
/** A line of LED dots along the underside of the main beams, one every `spacing` mm. */
export type BeamLeds = { color: HexColor; spacing: number; zone: BeamLedZone };

/** What a soffit is made of: wood, or the room's wall or ceiling surface. */
export const SOFFIT_MATERIALS = ['wood', 'walls', 'ceiling'] as const;
export type SoffitMaterial = (typeof SOFFIT_MATERIALS)[number];
/** `box`: a lowered band with an underside and a front; `curved`: a concave quarter round from the wall into the ceiling (a vault). */
export const SOFFIT_SHAPES = ['box', 'curved'] as const;
export type SoffitShape = (typeof SOFFIT_SHAPES)[number];

/** `flat`: a plain slab; `framed`: raised panels under a top rail; `fluted`: rounded vertical ribs. */
export const WALL_PANEL_STYLES = ['flat', 'framed', 'fluted'] as const;
export type WallPanelStyle = (typeof WALL_PANEL_STYLES)[number];
/** The textures a wall panel can be painted with, in its own color. */
export const WALL_PANEL_TEXTURES = [
    'plain',
    'wood-panels',
    'wood-slats',
    'acoustic-panels',
    'fabric-weave',
    'leather',
    'quilted',
    'marble',
    'concrete'
] as const satisfies readonly TextureId[];
export type WallPanelTexture = (typeof WALL_PANEL_TEXTURES)[number];

export const WALL_LIGHT_SHAPES = ['square', 'round'] as const;
export type WallLightShape = (typeof WALL_LIGHT_SHAPES)[number];

/** A list of outline edge ids, or every side wall. */
export type FeatureWalls = string[] | 'sides';

/** `locked`: the studio leaves the feature alone (drag, nudge, delete, inspector); the guest map ignores it. */
type FeatureBase = { id: string; color?: HexColor; locked?: true };

export type WallFeature = FeatureBase &
    (
        | { kind: 'led-line'; wall: string; points: [number, number][]; width: number }
        | { kind: 'light-band'; walls: FeatureWalls; z: number; height: number }
        | { kind: 'oval-window'; wall: string; s: number; z: number; w: number; h: number }
        /** Lamps every `spacing` along the walls, their middle `z` above the floor, `size` wide and tall. */
        | {
              kind: 'wall-lights';
              walls: FeatureWalls;
              spacing: number;
              z: number;
              size: number;
              shape: WallLightShape;
          }
        /**
         * A lattice on the walls from `from` to `to` mm along them (one run, continued around corners) and `z0`..`z1`
         * above the floor; `to`/`z1` past the end stop at the end. `follow: 'floor'` measures `z0` from the stepped floor
         * along the wall (the tier a row stands on), so the lower edge climbs with the rows; absent = the room floor.
         */
        | ({
              kind: 'led-lattice';
              walls: FeatureWalls;
              from: number;
              to: number;
              z0: number;
              z1: number;
              follow?: 'floor';
          } & LatticeLook)
        | { kind: 'light-frame'; x: number; y: number; size: number; rotation: number }
        | { kind: 'downlights'; spacing: number; inset: number }
        | { kind: 'cove'; inset: number; drop: number }
        /** A lattice on the ceiling: a `w` x `d` area centered at `x`, `y` (plan mm, degrees clockwise), or the whole ceiling. */
        | ({
              kind: 'ceiling-lattice';
              x: number;
              y: number;
              w: number;
              d: number;
              rotation: number;
              whole: boolean;
          } & LatticeLook)
        /**
         * Recessed coffers clipped to the ceiling outline: beams every `cell` mm, the coffers lit in `color`, or, with a
         * `palette`, each coffer in a color of it spread like a lattice's (`spread`, default random). The beams running
         * across (along the local x axis) may stand every `cellD` mm and hang `crossDepth` deep instead. `beamLeds` puts
         * a line of LED dots under the main beams.
         */
        | {
              kind: 'coffers';
              cell: number;
              beamWidth: number;
              beamDepth: number;
              rotation: number;
              beamColor: HexColor;
              light: CofferLight;
              palette?: HexColor[];
              spread?: LatticeSpread;
              cellD?: number;
              crossDepth?: number;
              beamLeds?: BeamLeds;
          }
        /** A soffit saved before `shape` existed is a box. */
        | {
              kind: 'soffit';
              walls: FeatureWalls;
              depth: number;
              drop: number;
              material: SoffitMaterial;
              shape?: SoffitShape;
          }
        | { kind: 'pilasters'; walls: FeatureWalls; spacing: number; width: number }
        /** Wainscoting from the floor up to `height`, standing `depth` out from the wall, in `color` (not lit). */
        | {
              kind: 'wall-panels';
              walls: FeatureWalls;
              height: number;
              depth: number;
              style: WallPanelStyle;
              texture: WallPanelTexture;
          }
        | { kind: 'curtain'; wall: string; from: number; to: number }
    );

/** The projection booth window (CIN-15126). */
export type BoothDesign = {
    /** Draws the window in the back wall. */
    window: boolean;
    /** Size of the window, mm. */
    width: number;
    height: number;
    /** Shows a projector behind the glass. */
    showProjector: boolean;
};

export const MAX_WALL_FEATURES = 100;

export type RoomLighting = {
    preset: LightingPreset;
    /** LED strips along the aisles at floor level. */
    aisleLights: boolean;
    /** Small lights on the riser of every stepped row. */
    stepLights: boolean;
    wallSconces: boolean;
    /** Fiber-optic star ceiling, painted with `surfaces.ceiling.accent`. */
    starCeiling: boolean;
    /** Wall wash color used by sconces and the show preset. */
    accentColor: HexColor;
    /** Mood: the color the ambient and key light lean toward, strongest in the show preset; absent = the preset's own. */
    ambientTint?: HexColor;
    /** Mood: the color of the wall wash (side lights, sconce up-wash); absent = `accentColor`. */
    washColor?: HexColor;
};

/** The color of the step and aisle lights: the tiers' own, else the accent. */
export const tierLedColor = (design: Pick<RoomDesign, 'tiers' | 'lighting'>): HexColor =>
    design.tiers.led?.color ?? design.lighting.accentColor;

export const SEAT_MAP_VIEWS = ['3d', '2d', '2d-only'] as const;
/** The seat map a guest gets for this room: a default for either view, or 2D with no 3D toggle. */
export type SeatMapView = (typeof SEAT_MAP_VIEWS)[number];

export type RoomDesign = {
    version: 1;
    /** The preset this design started from; `null` once nothing from it is left or it was custom. */
    themeId: string | null;
    surfaces: Record<SurfaceId, SurfaceMaterial>;
    lighting: RoomLighting;
    seatStyle: SeatStyle;
    /** Edge softness of the seats; documents saved before it existed read as `soft`. */
    seatRoundness: SeatRoundness;
    screen: {
        curtains: boolean;
        masking: ScreenMasking;
        /** An uploaded picture shown on the screen (https); `null` shows the movie of the showing. */
        imageUrl: string | null;
    };
    /** Finish of the stepped floor; documents saved before it existed read as everything off. */
    tiers: TierFinish;
    /** Seat details; documents saved before they existed read as plain upright seats. */
    seats: SeatDetails;
    /** Recliner extras: cupholder rings, tray tables. */
    seatExtras: SeatExtras;
    /** Per-category and per-seat colors and textures; documents saved before they existed read as none. */
    seatMaterials: SeatMaterials;
    /** Light and wall features; documents saved before they existed read as none. */
    features: WallFeature[];
    /** The projection booth window; documents saved before it existed read as today's window without a projector. */
    booth: BoothDesign;
    /** Free text labels the operator pins on the plan ("Bar", "Lounge"); plan space in mm. */
    annotations: RoomAnnotation[];
    /** Server ids of seats and objects locked against editing in the studio. */
    locks: { seatIds: number[]; objectIds: number[] };
    /**
     * Rooms synced from a ticket system: which sync the operator last took note of. Seats the sync dropped
     * before that time stop being reported. Absent until the operator acknowledges a sync.
     */
    ticketSync?: { acknowledgedAt: string };
    /** Guest seat map view; absent leaves the choice to the experiment and the visitor. */
    seatMap?: { view: SeatMapView };
};

export type RoomAnnotation = { id: string; x: number; y: number; text: string; color: HexColor };

const MAX_ANNOTATIONS = 100;
const MAX_ANNOTATION_LENGTH = 80;
const MAX_LOCKS = 5000;

const HEX = /^#[0-9a-fA-F]{6}$/;

const surface = (
    texture: TextureId,
    color: HexColor,
    accent: HexColor,
    overrides: Partial<SurfaceMaterial> = {}
): SurfaceMaterial => ({ texture, color, accent, scale: 1, roughness: 0.85, imageUrl: null, ...overrides });

/** How much darker than the upholstery a derived shell is: visibly harder, never near black. */
const SHELL_DARKENING = 0.2;

const DEFAULT_SEAT_SURFACE = surface('velvet', '#9b1b2c', '#3b0a12', { roughness: 0.75 });

export const DEFAULT_ROOM_DESIGN: RoomDesign = {
    version: 1,
    themeId: 'classic-velvet',
    surfaces: {
        walls: surface('acoustic-panels', '#2a1a22', '#140c11'),
        floor: surface('carpet-dots', '#1d1030', '#c9a24a'),
        ceiling: surface('plain', '#0c0a12', '#fff3c4', { roughness: 1 }),
        curtain: surface('velvet', '#8c1022', '#4a0610', { roughness: 0.7 }),
        seats: DEFAULT_SEAT_SURFACE,
        stage: surface('parquet', '#3a2618', '#24170e', { roughness: 0.6 }),
        seatShell: deriveSeatShell(DEFAULT_SEAT_SURFACE)
    },
    lighting: {
        preset: 'house',
        aisleLights: true,
        stepLights: true,
        wallSconces: true,
        starCeiling: false,
        accentColor: '#ffb561'
    },
    seatStyle: 'classic',
    seatRoundness: DEFAULT_SEAT_ROUNDNESS,
    screen: { curtains: true, masking: 'flat', imageUrl: null },
    tiers: {
        derivedBarriers: false,
        barrierCap: 'handrail',
        barrierLed: false,
        stairSideWalls: false,
        autoStairs: true
    },
    seats: { pose: 'upright', twoTone: false, headrestNumbers: false, endPanels: false },
    seatExtras: { trayTables: false, cupholders: true },
    seatMaterials: { byCategory: {}, bySeat: {} },
    features: [],
    booth: { window: true, width: 900, height: 420, showProjector: false },
    annotations: [],
    locks: { seatIds: [], objectIds: [] }
};

function clamp(value: unknown, min: number, max: number, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
}

function oneOf<T extends string, F extends T | null>(value: unknown, allowed: readonly T[], fallback: F): T | F {
    return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function hex(value: unknown, fallback: HexColor): HexColor {
    return typeof value === 'string' && HEX.test(value) ? (value.toLowerCase() as HexColor) : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A hex color mixed toward black by `amount` (0..1). */
export function darken(color: HexColor, amount: number): HexColor {
    const channel = (i: number) =>
        Math.round(parseInt(color.slice(1 + i * 2, 3 + i * 2), 16) * (1 - amount))
            .toString(16)
            .padStart(2, '0');
    return `#${channel(0)}${channel(1)}${channel(2)}`;
}

/** The shell of a two-tone seat when nothing was chosen: the upholstery, a shade darker. */
export function deriveSeatShell(seats: SurfaceMaterial): SurfaceMaterial {
    return {
        ...seats,
        texture: seats.texture === 'image' ? 'plain' : seats.texture,
        color: darken(seats.color, SHELL_DARKENING),
        accent: darken(seats.accent, SHELL_DARKENING),
        imageUrl: null
    };
}

/** An uploaded picture's URL: https and at most 2048 characters, else none. */
function httpsUrl(raw: unknown): string | null {
    return typeof raw === 'string' && /^https:\/\//.test(raw) && raw.length <= 2048 ? raw : null;
}

function normalizeSurface(raw: unknown, fallback: SurfaceMaterial): SurfaceMaterial {
    if (!isRecord(raw)) return { ...fallback };
    const imageUrl = httpsUrl(raw.imageUrl);
    const texture = oneOf(raw.texture, TEXTURE_IDS, fallback.texture);
    // An `image` surface whose URL did not survive falls back rather than rendering nothing.
    const imageFallback: TextureId = fallback.texture === 'image' ? 'plain' : fallback.texture;
    return {
        texture: texture === 'image' && !imageUrl ? imageFallback : texture,
        color: hex(raw.color, fallback.color),
        accent: hex(raw.accent, fallback.accent),
        scale: clamp(raw.scale, 0.25, 4, fallback.scale),
        roughness: clamp(raw.roughness, 0, 1, fallback.roughness),
        imageUrl
    };
}

function normalizeSurfaces(raw: Record<string, unknown>, fallback: RoomDesign): Record<SurfaceId, SurfaceMaterial> {
    const surfaces = Object.fromEntries(
        SURFACE_IDS.filter((id) => id !== 'seatShell').map((id) => [
            id,
            normalizeSurface(raw[id], fallback.surfaces[id])
        ])
    ) as Record<Exclude<SurfaceId, 'seatShell'>, SurfaceMaterial>;
    // A document from before the shell existed gets the upholstery in a darker shade.
    const seatShell = isRecord(raw.seatShell)
        ? normalizeSurface(raw.seatShell, fallback.surfaces.seatShell)
        : deriveSeatShell(surfaces.seats);
    return { ...surfaces, seatShell };
}

/** Tolerant parser for whatever the API returns in `cinemaRoom.design` (null, {}, older versions). */
export function normalizeRoomDesign(raw: unknown, fallback: RoomDesign = DEFAULT_ROOM_DESIGN): RoomDesign {
    if (!isRecord(raw)) return structuredCloneDesign(fallback);
    const surfaces = isRecord(raw.surfaces) ? raw.surfaces : {};
    const lighting = isRecord(raw.lighting) ? raw.lighting : {};
    const screen = isRecord(raw.screen) ? raw.screen : {};
    const tiers = isRecord(raw.tiers) ? raw.tiers : {};
    const seatDetails = isRecord(raw.seats) ? raw.seats : {};
    const seatExtras = isRecord(raw.seatExtras) ? raw.seatExtras : {};
    const booth = isRecord(raw.booth) ? raw.booth : {};
    const seatMapView = isRecord(raw.seatMap) ? oneOf(raw.seatMap.view, SEAT_MAP_VIEWS, null) : null;
    const acknowledgedAt = isRecord(raw.ticketSync) ? isoTime(raw.ticketSync.acknowledgedAt) : null;
    return {
        version: 1,
        themeId: typeof raw.themeId === 'string' && raw.themeId.length <= 64 ? raw.themeId : null,
        surfaces: normalizeSurfaces(surfaces, fallback),
        lighting: {
            preset: oneOf(lighting.preset, LIGHTING_PRESETS, fallback.lighting.preset),
            aisleLights: bool(lighting.aisleLights, fallback.lighting.aisleLights),
            stepLights: bool(lighting.stepLights, fallback.lighting.stepLights),
            wallSconces: bool(lighting.wallSconces, fallback.lighting.wallSconces),
            starCeiling: bool(lighting.starCeiling, fallback.lighting.starCeiling),
            accentColor: hex(lighting.accentColor, fallback.lighting.accentColor),
            ...optionalHex('ambientTint', lighting.ambientTint),
            ...optionalHex('washColor', lighting.washColor)
        },
        seatStyle: oneOf(raw.seatStyle, SEAT_STYLES, fallback.seatStyle),
        seatRoundness: oneOf(raw.seatRoundness, SEAT_ROUNDNESS, fallback.seatRoundness),
        screen: {
            curtains: bool(screen.curtains, fallback.screen.curtains),
            masking: oneOf(screen.masking, SCREEN_MASKINGS, fallback.screen.masking),
            imageUrl: httpsUrl(screen.imageUrl)
        },
        tiers: {
            derivedBarriers: bool(tiers.derivedBarriers, fallback.tiers.derivedBarriers),
            barrierCap: oneOf(tiers.barrierCap, BARRIER_CAPS, fallback.tiers.barrierCap),
            barrierLed: bool(tiers.barrierLed, fallback.tiers.barrierLed),
            stairSideWalls: bool(tiers.stairSideWalls, fallback.tiers.stairSideWalls),
            autoStairs: bool(tiers.autoStairs, fallback.tiers.autoStairs),
            ...normalizeTierPlatforms(tiers.platforms),
            ...(isRecord(tiers.led)
                ? {
                      led: {
                          ...optionalHex('color', tiers.led.color),
                          look: oneOf(tiers.led.look, TIER_LED_LOOKS, 'strip')
                      }
                  }
                : {})
        },
        seats: {
            pose: oneOf(seatDetails.pose, SEAT_POSES, fallback.seats.pose),
            twoTone: bool(seatDetails.twoTone, fallback.seats.twoTone),
            headrestNumbers: bool(seatDetails.headrestNumbers, fallback.seats.headrestNumbers),
            endPanels: bool(seatDetails.endPanels, fallback.seats.endPanels)
        },
        seatExtras: {
            trayTables: bool(seatExtras.trayTables, fallback.seatExtras.trayTables),
            cupholders: bool(seatExtras.cupholders, fallback.seatExtras.cupholders)
        },
        seatMaterials: normalizeSeatMaterials(raw.seatMaterials ?? raw.seatColors),
        features: normalizeFeatures(raw.features),
        booth: {
            window: bool(booth.window, fallback.booth.window),
            width: Math.round(clamp(booth.width, 300, 4000, fallback.booth.width)),
            height: Math.round(clamp(booth.height, 200, 2000, fallback.booth.height)),
            showProjector: bool(booth.showProjector, fallback.booth.showProjector)
        },
        annotations: normalizeAnnotations(raw.annotations),
        locks: {
            seatIds: normalizeIds(isRecord(raw.locks) ? raw.locks.seatIds : undefined),
            objectIds: normalizeIds(isRecord(raw.locks) ? raw.locks.objectIds : undefined)
        },
        ...(acknowledgedAt === null ? {} : { ticketSync: { acknowledgedAt } }),
        ...(seatMapView === null ? {} : { seatMap: { view: seatMapView } })
    };
}

const ID_KEY = /^\d{1,12}$/;

const hexOf = (value: unknown): HexColor | undefined =>
    typeof value === 'string' && HEX.test(value) ? (value.toLowerCase() as HexColor) : undefined;

/** `{ [key]: color }` for a valid hex, `{}` otherwise, so an optional color key stays absent. */
function optionalHex<K extends string>(key: K, value: unknown): Partial<Record<K, HexColor>> {
    const color = hexOf(value);
    return color ? ({ [key]: color } as Record<K, HexColor>) : {};
}

const SEAT_TEXTURES: readonly string[] = TEXTURE_IDS.filter((id) => id !== 'image');

/** One part; a bare hex string is the form of documents saved before textures (`{ cushion: '#aabbcc' }`). */
function normalizePartMaterial(raw: unknown): SeatPartMaterial | undefined {
    if (typeof raw === 'string') {
        const color = hexOf(raw);
        return color ? { color } : undefined;
    }
    if (!isRecord(raw)) return undefined;
    const color = hexOf(raw.color);
    const accent = hexOf(raw.accent);
    const texture =
        typeof raw.texture === 'string' && SEAT_TEXTURES.includes(raw.texture) ? (raw.texture as TextureId) : undefined;
    if (!color && !texture && !accent) return undefined;
    return { ...(color ? { color } : {}), ...(texture ? { texture } : {}), ...(accent ? { accent } : {}) };
}

/** The form fields of an entry that hold a valid value, in a fixed order; unknown values are dropped, not defaulted. */
function normalizeSeatForm(raw: Record<string, unknown>): SeatFormSpec {
    const out: SeatFormSpec = {};
    if (typeof raw.style === 'string' && (SEAT_STYLES as readonly string[]).includes(raw.style)) {
        out.style = raw.style as SeatStyle;
    }
    if (typeof raw.roundness === 'string' && (SEAT_ROUNDNESS as readonly string[]).includes(raw.roundness)) {
        out.roundness = raw.roundness as SeatRoundness;
    }
    if (typeof raw.pose === 'string' && (SEAT_POSES as readonly string[]).includes(raw.pose)) {
        out.pose = raw.pose as SeatPose;
    }
    for (const field of SEAT_FORM_FLAGS) {
        const value = raw[field];
        if (typeof value === 'boolean') out[field] = value;
    }
    return out;
}

/** One material/form entry as the design stores it; `null` when it holds nothing valid. */
export function normalizeSeatMaterialSpec(value: unknown): SeatMaterialSpec | null {
    if (!isRecord(value)) return null;
    const cushion = normalizePartMaterial(value.cushion);
    const shell = normalizePartMaterial(value.shell);
    const form = normalizeSeatForm(value);
    if (!cushion && !shell && Object.keys(form).length === 0) return null;
    return { ...form, ...(cushion ? { cushion } : {}), ...(shell ? { shell } : {}) };
}

function normalizeMaterialEntries(raw: unknown, max: number, maxBytes: number): Record<string, SeatMaterialSpec> {
    const out: Record<string, SeatMaterialSpec> = {};
    if (!isRecord(raw)) return out;
    let count = 0;
    let bytes = 0;
    for (const [key, value] of Object.entries(raw)) {
        if (count >= max) break;
        if (!ID_KEY.test(key)) continue;
        const entry = normalizeSeatMaterialSpec(value);
        if (!entry) continue;
        bytes += JSON.stringify(entry).length + key.length + 4;
        if (bytes > maxBytes) break;
        out[key] = entry;
        count += 1;
    }
    return out;
}

function normalizeSeatMaterials(raw: unknown): SeatMaterials {
    const materials = isRecord(raw) ? raw : {};
    return {
        byCategory: normalizeMaterialEntries(
            materials.byCategory,
            MAX_CATEGORY_MATERIAL_ENTRIES,
            MAX_CATEGORY_MATERIAL_BYTES
        ),
        bySeat: normalizeMaterialEntries(materials.bySeat, MAX_SEAT_MATERIAL_ENTRIES, MAX_SEAT_MATERIAL_BYTES)
    };
}

const FEATURE_KINDS: readonly string[] = WALL_FEATURE_KINDS;
const MAX_FEATURE_POINTS = 32;
const MAX_FEATURE_WALLS = 16;
const MAX_ID_LENGTH = 16;
const MAX_ALONG_MM = 200_000;
const MAX_PLAN_MM = 100_000;
/** A lattice may climb a light wall up to here (an IMAX hall is 25 m high), mm. */
export const MAX_LATTICE_HEIGHT_MM = 60_000;
/** The largest lattice cell and beam (an IMAX light wall has 3.6 m triangles on 0.4 m beams), mm. */
export const MAX_LATTICE_CELL_MM = 6000;
export const MAX_LATTICE_BEAM_MM = 600;

const int = (value: unknown, min: number, max: number, fallback: number): number =>
    Math.round(clamp(value, min, max, fallback));

const isEdgeId = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0 && value.length <= MAX_ID_LENGTH;

function featureWalls(value: unknown): FeatureWalls | null {
    if (value === 'sides') return 'sides';
    if (!Array.isArray(value)) return null;
    const walls = [...new Set(value.filter(isEdgeId))].slice(0, MAX_FEATURE_WALLS);
    return walls.length > 0 ? walls : null;
}

/** The default colors of a new lattice: the blue, orange, yellow and magenta of an IMAX light wall. */
export const DEFAULT_LATTICE_PALETTE: HexColor[] = ['#2f6bff', '#ff7a1a', '#ffc53d', '#e0369b'];

/** The valid colors of a palette, at most `MAX_LATTICE_COLORS`; empty when there is none. */
function normalizePalette(raw: unknown): HexColor[] {
    return Array.isArray(raw)
        ? raw
              .filter((color): color is string => typeof color === 'string' && HEX.test(color))
              .slice(0, MAX_LATTICE_COLORS)
              .map((color) => color.toLowerCase() as HexColor)
        : [];
}

function normalizeLatticeLook(raw: Record<string, unknown>): LatticeLook {
    const palette = normalizePalette(raw.palette);
    return {
        pattern: oneOf(raw.pattern, LATTICE_PATTERNS, 'triangle'),
        cell: int(raw.cell, 300, MAX_LATTICE_CELL_MM, 1200),
        beam: int(raw.beam, 20, MAX_LATTICE_BEAM_MM, 80),
        beamColor: hex(raw.beamColor, '#141418'),
        palette: palette.length > 0 ? palette : [...DEFAULT_LATTICE_PALETTE],
        spread: oneOf(raw.spread, LATTICE_SPREADS, 'random'),
        glow: Math.round(clamp(raw.glow, 0, 1, 0.7) * 100) / 100,
        seed: int(raw.seed, 0, 9999, 0),
        ...(raw.columns === true ? { columns: true as const } : {}),
        ...(typeof raw.inner === 'string' && HEX.test(raw.inner) ? { inner: raw.inner.toLowerCase() as HexColor } : {}),
        ...(raw.nodes === true ? { nodes: true as const } : {})
    };
}

/** One feature, clamped, or null when it is unusable (unknown kind, missing wall, no points). */
function normalizeFeature(raw: unknown, index: number): WallFeature | null {
    if (!isRecord(raw) || typeof raw.kind !== 'string' || !FEATURE_KINDS.includes(raw.kind)) return null;
    const id = typeof raw.id === 'string' && raw.id.length > 0 ? raw.id.slice(0, MAX_ID_LENGTH) : `f${index + 1}`;
    const color =
        typeof raw.color === 'string' && HEX.test(raw.color) ? (raw.color.toLowerCase() as HexColor) : undefined;
    const base = { id, ...(color ? { color } : {}), ...(raw.locked === true ? { locked: true as const } : {}) };
    switch (raw.kind) {
        case 'led-line': {
            if (!isEdgeId(raw.wall) || !Array.isArray(raw.points)) return null;
            const points: [number, number][] = [];
            for (const point of raw.points.slice(0, MAX_FEATURE_POINTS)) {
                if (Array.isArray(point) && Number.isFinite(point[0]) && Number.isFinite(point[1])) {
                    points.push([int(point[0], 0, MAX_ALONG_MM, 0), int(point[1], 0, 20_000, 0)]);
                }
            }
            return points.length >= 2
                ? { ...base, kind: 'led-line', wall: raw.wall, points, width: int(raw.width, 10, 300, 40) }
                : null;
        }
        case 'light-band': {
            const walls = featureWalls(raw.walls);
            return walls
                ? {
                      ...base,
                      kind: 'light-band',
                      walls,
                      z: int(raw.z, 0, 20_000, 2500),
                      height: int(raw.height, 10, 2000, 80)
                  }
                : null;
        }
        case 'oval-window':
            return isEdgeId(raw.wall)
                ? {
                      ...base,
                      kind: 'oval-window',
                      wall: raw.wall,
                      s: int(raw.s, 0, MAX_ALONG_MM, 0),
                      z: int(raw.z, 0, 20_000, 2000),
                      w: int(raw.w, 100, 6000, 1200),
                      h: int(raw.h, 100, 6000, 800)
                  }
                : null;
        case 'light-frame':
            return {
                ...base,
                kind: 'light-frame',
                x: int(raw.x, -MAX_PLAN_MM, MAX_PLAN_MM, 0),
                y: int(raw.y, -MAX_PLAN_MM, 0, -5000),
                size: int(raw.size, 200, 8000, 2000),
                rotation: ((int(raw.rotation, -3600, 3600, 0) % 360) + 360) % 360
            };
        case 'downlights':
            return {
                ...base,
                kind: 'downlights',
                spacing: int(raw.spacing, 500, 6000, 1500),
                inset: int(raw.inset, 200, 6000, 800)
            };
        case 'cove':
            return { ...base, kind: 'cove', inset: int(raw.inset, 100, 4000, 400), drop: int(raw.drop, 0, 1500, 150) };
        case 'led-lattice': {
            const walls = featureWalls(raw.walls);
            if (!walls) return null;
            const from = int(raw.from, 0, MAX_ALONG_MM, 0);
            const to = int(raw.to, 0, MAX_ALONG_MM, MAX_ALONG_MM);
            const z0 = int(raw.z0, 0, MAX_LATTICE_HEIGHT_MM, 0);
            const z1 = int(raw.z1, 0, MAX_LATTICE_HEIGHT_MM, 20_000);
            return {
                ...base,
                kind: 'led-lattice',
                walls,
                from: Math.min(from, to),
                to: Math.max(from, to),
                z0: Math.min(z0, z1),
                z1: Math.max(z0, z1),
                ...(raw.follow === 'floor' ? { follow: 'floor' as const } : {}),
                ...normalizeLatticeLook(raw)
            };
        }
        case 'ceiling-lattice':
            return {
                ...base,
                kind: 'ceiling-lattice',
                x: int(raw.x, -MAX_PLAN_MM, MAX_PLAN_MM, 0),
                y: int(raw.y, -MAX_PLAN_MM, 0, -5000),
                w: int(raw.w, 500, 60_000, 6000),
                d: int(raw.d, 500, 60_000, 4000),
                rotation: ((int(raw.rotation, -3600, 3600, 0) % 360) + 360) % 360,
                whole: raw.whole === true,
                ...normalizeLatticeLook(raw)
            };
        case 'coffers': {
            // Absent stays absent: a coffer saved before palettes reads back unchanged.
            const palette = normalizePalette(raw.palette);
            return {
                ...base,
                kind: 'coffers',
                cell: int(raw.cell, 600, 6000, 2400),
                beamWidth: int(raw.beamWidth, 50, 1000, 250),
                beamDepth: int(raw.beamDepth, 50, 1500, 400),
                rotation: ((int(raw.rotation, -3600, 3600, 0) % 360) + 360) % 360,
                beamColor: hex(raw.beamColor, '#1c1b20'),
                light: oneOf(raw.light, COFFER_LIGHTS, 'rim'),
                ...(palette.length > 0 ? { palette, spread: oneOf(raw.spread, LATTICE_SPREADS, 'random') } : {}),
                ...(raw.cellD === undefined ? {} : { cellD: int(raw.cellD, 600, 6000, 2400) }),
                ...(raw.crossDepth === undefined ? {} : { crossDepth: int(raw.crossDepth, 50, 1500, 400) }),
                ...(isRecord(raw.beamLeds)
                    ? {
                          beamLeds: {
                              color: hex(raw.beamLeds.color, '#ff2a3a'),
                              spacing: int(raw.beamLeds.spacing, 100, 2000, 300),
                              zone: oneOf(raw.beamLeds.zone, BEAM_LED_ZONES, 'all')
                          }
                      }
                    : {})
            };
        }
        case 'soffit': {
            const walls = featureWalls(raw.walls);
            return walls
                ? {
                      ...base,
                      kind: 'soffit',
                      walls,
                      depth: int(raw.depth, 100, 3000, 500),
                      drop: int(raw.drop, 50, 2000, 250),
                      material: oneOf(raw.material, SOFFIT_MATERIALS, 'wood'),
                      // Absent stays absent, so a soffit saved before shapes reads back unchanged.
                      ...(typeof raw.shape === 'string' && (SOFFIT_SHAPES as readonly string[]).includes(raw.shape)
                          ? { shape: raw.shape as SoffitShape }
                          : {})
                  }
                : null;
        }
        case 'wall-lights': {
            const walls = featureWalls(raw.walls);
            return walls
                ? {
                      ...base,
                      kind: 'wall-lights',
                      walls,
                      spacing: int(raw.spacing, 500, 20_000, 3000),
                      z: int(raw.z, 0, 20_000, 2200),
                      size: int(raw.size, 100, 1500, 300),
                      shape: oneOf(raw.shape, WALL_LIGHT_SHAPES, 'square')
                  }
                : null;
        }
        case 'wall-panels': {
            const walls = featureWalls(raw.walls);
            return walls
                ? {
                      ...base,
                      kind: 'wall-panels',
                      walls,
                      height: int(raw.height, 100, 4000, 1000),
                      depth: int(raw.depth, 10, 200, 30),
                      style: oneOf(raw.style, WALL_PANEL_STYLES, 'flat'),
                      texture: oneOf(raw.texture, WALL_PANEL_TEXTURES, 'plain')
                  }
                : null;
        }
        case 'pilasters': {
            const walls = featureWalls(raw.walls);
            return walls
                ? {
                      ...base,
                      kind: 'pilasters',
                      walls,
                      spacing: int(raw.spacing, 300, 4000, 1200),
                      width: int(raw.width, 20, 400, 60)
                  }
                : null;
        }
        case 'curtain': {
            if (!isEdgeId(raw.wall)) return null;
            const from = int(raw.from, 0, MAX_ALONG_MM, 0);
            const to = int(raw.to, 0, MAX_ALONG_MM, from + 3000);
            return { ...base, kind: 'curtain', wall: raw.wall, from: Math.min(from, to), to: Math.max(from, to) };
        }
        default:
            return null;
    }
}

function normalizeFeatures(raw: unknown): WallFeature[] {
    if (!Array.isArray(raw)) return [];
    const features: WallFeature[] = [];
    const ids = new Set<string>();
    for (const [index, item] of raw.slice(0, MAX_WALL_FEATURES).entries()) {
        const feature = normalizeFeature(item, index);
        if (!feature || ids.has(feature.id)) continue;
        ids.add(feature.id);
        features.push(feature);
    }
    return features;
}

/** An ISO 8601 time as given, or `null` when it does not parse. */
function isoTime(raw: unknown): string | null {
    return typeof raw === 'string' && raw.length <= 40 && Number.isFinite(Date.parse(raw)) ? raw : null;
}

function normalizeAnnotations(raw: unknown): RoomAnnotation[] {
    if (!Array.isArray(raw)) return [];
    const annotations: RoomAnnotation[] = [];
    for (const item of raw.slice(0, MAX_ANNOTATIONS)) {
        if (!isRecord(item) || typeof item.id !== 'string' || typeof item.text !== 'string') continue;
        const text = item.text.trim().slice(0, MAX_ANNOTATION_LENGTH);
        if (text === '' || !Number.isFinite(item.x) || !Number.isFinite(item.y)) continue;
        annotations.push({
            id: item.id.slice(0, 64),
            x: item.x as number,
            y: item.y as number,
            text,
            color: hex(item.color, '#ffffff')
        });
    }
    return annotations;
}

const MAX_ROW_NAME_LENGTH = 32;
const MAX_PLATFORM_CUT = 50_000;
const MAX_PLATFORM_FRONT = 10_000;

/** The platform entries that name a row and set something, first per row and level; `{}` when none, so the key stays absent. */
function normalizeTierPlatforms(raw: unknown): { platforms?: TierPlatformSpec[] } {
    if (!Array.isArray(raw)) return {};
    const platforms: TierPlatformSpec[] = [];
    const seen = new Set<string>();
    for (const item of raw.slice(0, MAX_TIER_PLATFORMS)) {
        if (!isRecord(item) || typeof item.row !== 'string') continue;
        const row = item.row.trim();
        if (row === '' || row.length > MAX_ROW_NAME_LENGTH) continue;
        const level = isEdgeId(item.level) ? item.level : undefined;
        const length = (value: unknown, max: number) =>
            typeof value === 'number' && Number.isFinite(value) ? int(value, 0, max, 0) : undefined;
        const left = length(item.left, MAX_PLATFORM_CUT);
        const right = length(item.right, MAX_PLATFORM_CUT);
        const front = length(item.front, MAX_PLATFORM_FRONT);
        const ledColor = isRecord(item.led) ? hexOf(item.led.color) : undefined;
        const key = `${level ?? ''}\n${row}`;
        if ((!left && !right && front === undefined && !ledColor) || seen.has(key)) continue;
        seen.add(key);
        platforms.push({
            row,
            ...(level ? { level } : {}),
            ...(left ? { left } : {}),
            ...(right ? { right } : {}),
            ...(front !== undefined ? { front } : {}),
            ...(ledColor ? { led: { color: ledColor } } : {})
        });
    }
    return platforms.length > 0 ? { platforms } : {};
}

function normalizeIds(raw: unknown): number[] {
    if (!Array.isArray(raw)) return [];
    return [...new Set(raw.filter((id): id is number => Number.isInteger(id) && id > 0))].slice(0, MAX_LOCKS);
}

function structuredCloneDesign(design: RoomDesign): RoomDesign {
    return JSON.parse(JSON.stringify(design)) as RoomDesign;
}
